/**
 * Sisumail API Client V2
 */
import { parseRouteKey, ROUTE_KEYS, ROUTE_META, } from '@sisumail/contracts';
import * as v from 'valibot';
import { ContractClient, } from './client';
export { ApiClientError, ContractClient, ContractViolationError, deriveValidity, } from './client';
// =============================================================================
// =============================================================================
/**
 * Build a path from a RouteKey, substituting :param placeholders
 */
function buildPath(key, params) {
    let { path } = parseRouteKey(key);
    if (params) {
        for (const [k, v] of Object.entries(params)) {
            path = path.replace(`:${k}`, encodeURIComponent(v));
        }
    }
    return path;
}
export class SisumailClient extends ContractClient {
    constructor(config) {
        super({
            env: 'production',
            credentials: 'include', // DIAMOND: Ensure cookies are sent by default
            ...config,
        });
    }
    /**
     * Helper to construct EndpointDef from RouteKey + Meta.
     * Guards against missing schemas which should not happen in Phase 3.
     */
    getEndpoint(key) {
        const meta = ROUTE_META[key];
        const { method, path } = parseRouteKey(key);
        if (!meta.responseSchema) {
            // Response schema is strictly required
            throw new Error(`Missing response schema for route: ${key}`);
        }
        // Strict behavior: if no requestSchema, we expect no input (undefined)
        // This prevents implicit 'any' payloads.
        const requestSchema = meta.requestSchema || v.undefined();
        return {
            alias: key,
            method: method,
            path,
            request: requestSchema,
            response: meta.responseSchema,
            metadata: {
                auth: meta.auth !== 'public', // simple mapping
                ...(meta.smoke !== undefined ? { smoke: meta.smoke } : {}),
                ...(meta.danger !== undefined ? { danger: meta.danger } : {}),
            },
        };
    }
    // =========================================================================
    // Auth Routes (Migrated)
    // =========================================================================
    auth = {
        reserve: (params) => this.call(this.getEndpoint(ROUTE_KEYS.AUTH_RESERVE), params),
        check: (username) => this.call(this.getEndpoint(ROUTE_KEYS.AUTH_CHECK_USERNAME), { username }),
        challenge: (params) => this.call(this.getEndpoint(ROUTE_KEYS.AUTH_CHALLENGE), params),
        prove: (params) => this.call(this.getEndpoint(ROUTE_KEYS.AUTH_PROVE), params),
        register: (params) => this.call(this.getEndpoint(ROUTE_KEYS.AUTH_REGISTER), params),
        logout: () => this.call(this.getEndpoint(ROUTE_KEYS.AUTH_LOGOUT), {}),
        listSessions: () => this.call(this.getEndpoint(ROUTE_KEYS.SESSIONS_LIST), undefined),
        revokeSession: (id) => this.call(this.getEndpoint(ROUTE_KEYS.SESSIONS_DELETE), { id }),
        revokeOthers: () => this.call(this.getEndpoint(ROUTE_KEYS.SESSIONS_REVOKE_OTHERS), undefined),
    };
    identity = {
        status: (include_crypto) => {
            const params = include_crypto ? { include_crypto: 'true' } : undefined;
            return this.call(this.getEndpoint(ROUTE_KEYS.IDENTITY_STATUS), params);
        },
        exists: async (username) => {
            const result = (await this.call(this.getEndpoint(ROUTE_KEYS.AUTH_CHECK_USERNAME), {
                username,
            }));
            return { ...result, data: { exists: !result.data.available } };
        },
    };
    // =========================================================================
    // Device Routes (Migrated)
    // =========================================================================
    devices = {
        requestPairing: (params) => this.call(this.getEndpoint(ROUTE_KEYS.DEVICES_PAIR_REQUEST), params),
        finalizeReader: (params) => this.call(this.getEndpoint(ROUTE_KEYS.DEVICES_FINALIZE_READER), params),
        claimPairing: (params) => this.call(this.getEndpoint(ROUTE_KEYS.DEVICES_PAIR_CLAIM), params),
        listRequests: () => this.call(this.getEndpoint(ROUTE_KEYS.DEVICES_REQUESTS), undefined),
        list: () => this.call(this.getEndpoint(ROUTE_KEYS.DEVICES_LIST), undefined),
        revoke: (id) => this.call(this.getEndpoint(ROUTE_KEYS.DEVICES_REVOKE), { id }),
        /**
         * DIAMOND: Connect to the device stream (SSE)
         * Returns the raw Response object to allow streaming reading
         */
        connectStream: async (signal) => {
            const { method } = parseRouteKey(ROUTE_KEYS.DEVICES_STREAM);
            // Re-implement basic path building since we removed buildPath helper (or we can keep it?)
            // buildPath was outside the class, so we can keep using it if we keep the function.
            // But we deleted the function? No, buildPath is a standalone function at line 61.
            // I should check if buildPath was deleted. My replacement targets the class body.
            // Wait, buildPath helper function was at line 61. It is outside the class.
            // I will use it.
            const path = buildPath(ROUTE_KEYS.DEVICES_STREAM);
            const url = `${this.config.baseUrl}${path}`;
            const headers = {
                Accept: 'text/event-stream',
            };
            if (this.config.deviceKey) {
                headers['Authorization'] =
                    `DeviceKey ${this.config.deviceKey.id}:${this.config.deviceKey.secret}`;
            }
            // We bypass the standard request() wrapper because we need the raw stream
            // and we don't want the default JSON parsing logic
            return fetch(url, {
                method,
                headers,
                signal: signal || null,
            });
        },
    };
    inbox = {
        list: (params = {}) => this.call(this.getEndpoint(ROUTE_KEYS.INBOX_LIST), params),
        get: (id) => this.call(this.getEndpoint(ROUTE_KEYS.INBOX_GET), { id }),
    };
    async log(entry) {
        await this.call(this.getEndpoint(ROUTE_KEYS.LOG_INGEST), entry);
    }
    billing = {
        checkout: () => this.call(this.getEndpoint(ROUTE_KEYS.BILLING_CHECKOUT), {}),
        verify: (paymentId) => this.call(this.getEndpoint(ROUTE_KEYS.BILLING_VERIFY), { paymentId }),
        simulate: (username) => this.call(this.getEndpoint(ROUTE_KEYS.BILLING_DEV_SIMULATE), { username }),
    };
    admin = {
        stats: () => this.call(this.getEndpoint(ROUTE_KEYS.ADMIN_STATS), undefined),
        provision: (username) => this.call(this.getEndpoint(ROUTE_KEYS.ADMIN_PROVISION), { username }),
        listIdentities: (limit = 50, offset = 0, search) => {
            return this.call(this.getEndpoint(ROUTE_KEYS.ADMIN_LIST_IDENTITIES), {
                limit,
                offset,
                search,
            });
        },
        getIdentity: (username) => this.call(this.getEndpoint(ROUTE_KEYS.ADMIN_GET_IDENTITY), { username }),
        activateIdentity: (username) => this.call(this.getEndpoint(ROUTE_KEYS.ADMIN_ACTIVATE_IDENTITY), { username }),
        revokeIdentity: (username) => this.call(this.getEndpoint(ROUTE_KEYS.ADMIN_DELETE_IDENTITY), { username }),
        logs: (limit = 100, offset = 0, level, source, requestId, userId) => {
            return this.call(this.getEndpoint(ROUTE_KEYS.ADMIN_LOGS), {
                limit,
                offset,
                level,
                source,
                requestId,
                userId,
            });
        },
    };
}
// ApiClientError imported from ./client
export function createClient(config) {
    return new SisumailClient(config);
}
export function createSisumailClient(options) {
    const config = {
        baseUrl: options.baseUrl,
        env: options.env,
        onRequest: (init) => {
            let h = (init.headers || {});
            // Add custom headers
            if (options.customHeaders) {
                h = { ...h, ...options.customHeaders };
            }
            // Add admin auth if provided
            if (options.adminSecret) {
                h['Authorization'] = `Bearer ${options.adminSecret}`;
            }
            init.headers = h;
            // Chain user-provided onRequest
            if (options.onRequest) {
                init = options.onRequest(init);
            }
            return init;
        },
        onContractViolation: (error) => {
            // Version mismatch gets special handling
            if (error.name === 'ContractVersionMismatchError') {
                if (options.onVersionMismatch) {
                    options.onVersionMismatch();
                }
                else {
                    console.warn('[API] Version mismatch - update required');
                }
                return;
            }
            // Standard schema violation
            if (options.onSchemaViolation) {
                options.onSchemaViolation(error);
            }
            else {
                console.error('[API] Schema violation:', error.getSummary?.() || error.message);
            }
        },
    };
    // Add optional properties only if defined
    if (options.deviceKey) {
        config.deviceKey = options.deviceKey;
    }
    if (options.onError) {
        config.onError = options.onError;
    }
    return new SisumailClient(config);
}
export default SisumailClient;
//# sourceMappingURL=index.js.map